import React, { useState, useEffect, Suspense, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, ChevronDown, Check, Sparkles, Maximize2, Minimize2, Monitor, Tablet, Smartphone, Globe, Sun, Moon, ArrowUpRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { adminBuiltInVariantsApi } from '../services/api';

type Viewport = 'mobile' | 'tablet' | 'desktop';

interface VariantInfo {
  _id: string;
  variantId: string;
  name: string;
  description: string;
  tier: 'freemium' | 'premium';
  isBuiltIn?: boolean;
  isPaid: boolean;
  price: number;
  previewImageUrl?: string;
  category?: string;  
  tags?: string[];
  isActive?: boolean;
  isPublic?: boolean;
  isTaken?: boolean;
  component?: React.ComponentType<any>;
}

const mockCompany = {
  _id: 'preview-company',
  name: 'Empresa Demo',
  slug: 'preview-demo',
  description: 'Esta é uma visualização de demonstração',
  logo: '',
  website: '',
  phone: '+55 11 98765-4321',
  email: 'contato@empresademo.com',
  address: 'São Paulo, SP - Brasil',
};

const mockServices = [
  { _id: '1', image: 'https://i.pinimg.com/736x/ff/e6/37/ffe637232891eefebed99ef52d8a6583.jpg', name: 'Serviço 1', description: 'Descrição do serviço 1', price: 100 },
  { _id: '2', image: 'https://i1-c.pinimg.com/1200x/6d/94/12/6d9412bdb8a8adedc23932bd82e2ba9b.jpg', name: 'Serviço 2', description: 'Descrição do serviço 2', price: 200 },
  { _id: '3', image: 'https://i1-c.pinimg.com/1200x/6d/94/12/6d9412bdb8a8adedc23932bd82e2ba9b.jpg', name: 'Serviço 3', description: 'Descrição do serviço 3', price: 300 },
];

const mockProducts = [
  { _id: '1', image: 'https://i1-c.pinimg.com/1200x/6d/94/12/6d9412bdb8a8adedc23932bd82e2ba9b.jpg', name: 'Produto 1', description: 'Descrição do produto 1', price: 100 },
  { _id: '2', image: 'https://i.pinimg.com/736x/98/97/f6/9897f6737ffca2fd39e8185adb1b4b6e.jpg', name: 'Produto 2', description: 'Descrição do produto 2', price: 200 },
  { _id: '3', image: 'https://i1-c.pinimg.com/736x/85/0e/89/850e8969446a1494593a7445f5c6e4c0.jpg', name: 'Produto 3', description: 'Descrição do produto 3', price: 300 },
];

const mockBundles = [
  {
    _id: 'bundle-1',
    name: 'Premium Combo',
    type: 'Combo',
    description: 'Pacote completo de serviços',
    price: 5000,
    image: 'https://i1-c.pinimg.com/1200x/6d/94/12/6d9412bdb8a8adedc23932bd82e2ba9b.jpg',
    items: []
  },
  {
    _id: 'sub-1',
    name: 'Professional Plan',
    type: 'Subscription',
    description: 'Assinatura mensal profissional',
    price: 1500,
    billingCycle: 'Mensal',
    image: 'https://i1-c.pinimg.com/1200x/6d/94/12/6d9412bdb8a8adedc23932bd82e2ba9b.jpg',
    includedLimits: [{ description: 'Requisições', maxValue: 50, unit: '/mês' }]
  }
];

const variantModules = import.meta.glob<{ default: React.ComponentType<any> }>(
  '../templates/public-portal/variants/*.tsx',
  { eager: true }
);

const getComponentForVariant = (variantId: string): React.ComponentType<any> | undefined => {
  const pascalCase = variantId
    .split(/[-_]/)
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');

  const possibleFileNames = [
    `../templates/public-portal/variants/${pascalCase}Portal.tsx`,
    `../templates/public-portal/variants/${pascalCase}Digital.tsx`,
    `../templates/public-portal/variants/${pascalCase}.tsx`,
  ];

  for (const fileName of possibleFileNames) {
    if (variantModules[fileName]) {
      return variantModules[fileName].default;
    }
  }
  return undefined;
};

export const PublicPortalGallery: React.FC = () => {
  const navigate = useNavigate();
  
  // Theme state: dark por padrão
  const [isDarkMode, setIsDarkMode] = useState(false);

  const [variants, setVariants] = useState<VariantInfo[]>([]);
  const [filteredVariants, setFilteredVariants] = useState<VariantInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [previewVariant, setPreviewVariant] = useState<VariantInfo | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentViewport, setCurrentViewport] = useState<Viewport>('desktop');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  

  const [selectedTier, setSelectedTier] = useState<'all' | 'freemium' | 'premium'>('all');

    // New refs/state for rendering the hidden-categories dropdown outside of the scroll container
  const categoryToggleRef = useRef<HTMLButtonElement | null>(null);
  const categoryDropdownRef = useRef<HTMLDivElement | null>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const CATEGORIES_LIMIT = 5;
  const TAGS_LIMIT = 5;
  useEffect(() => {
    if (!showCategoryDropdown) return;
    const updatePos = () => {
      const btn = categoryToggleRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY + 6,
        left: rect.left + window.scrollX,
        width: Math.max(200, rect.width),
      });
    };
    updatePos();
    const onScrollOrResize = () => updatePos();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [showCategoryDropdown]);
   useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (showCategoryDropdown) {
        if (
          categoryToggleRef.current && categoryDropdownRef.current &&
          !categoryToggleRef.current.contains(t) &&
          !categoryDropdownRef.current.contains(t)
        ) {
          setShowCategoryDropdown(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCategoryDropdown]);
  useEffect(() => {
    loadVariants();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, selectedCategory, selectedTags, selectedTier, variants]);

    const loadVariants = async () => {
    try {
      setLoading(true);
      setError(null);
      const builtInData = await adminBuiltInVariantsApi.getAll();

      const combined = builtInData.map((builtIn: any) => {
        let component = undefined;

        if (builtIn.isActive && builtIn.variantId) {
          component = getComponentForVariant(builtIn.variantId);
        }

        return {
          _id: builtIn._id?.$oid || builtIn._id,
          variantId: builtIn.variantId,
          id: builtIn.variantId,
          name: builtIn.name,
          description: builtIn.description,
          tier: builtIn.tier || 'freemium',
          previewImageUrl: builtIn.previewImageUrl,
          category: builtIn.category,
          tags: Array.isArray(builtIn.tags) ? builtIn.tags : [],
          isActive: builtIn.isActive ?? true,
          isPublic: builtIn.isPublic ?? true,
          isPaid: builtIn.isPaid ?? false,
          price: builtIn.price ?? 0,
          isTaken: builtIn.isTaken ?? false,
          component: component,
        } as VariantInfo;
      });

      setVariants(combined);
      setFilteredVariants(combined);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erro ao carregar templates';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...variants];

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(v => 
        v.name.toLowerCase().includes(term) || 
        (v.description && v.description.toLowerCase().includes(term))
      );
    }

    if (selectedTier !== 'all') {
      result = result.filter(v => v.tier === selectedTier);
    }

    if (selectedCategory !== 'all') {
      result = result.filter(v => v.category === selectedCategory);
    }

    if (selectedTags.length > 0) {
      result = result.filter(v => 
        selectedTags.some(tag => v.tags?.includes(tag))
      );
    }

    result = result.filter(v => !(v.tier === 'premium' && v.isTaken));
    setFilteredVariants(result);
  };

  const categories = ['Todos', ...new Set(
    variants.map(v => v.category).filter((c): c is string => Boolean(c))
  )];

  const getAllTags = (): string[] => {
    const tags = new Set<string>();
    variants.forEach(v => {
      if (Array.isArray(v.tags)) {
        v.tags.forEach(tag => tag && typeof tag === 'string' && tags.add(tag));
      }
    });
    return Array.from(tags).sort();
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const openPreview = (variant: VariantInfo) => {
    if (!variant.component) {
      toast.error('Componente de preview não disponível para este template');
      return;
    }
    setPreviewVariant(variant);
    setCurrentViewport('desktop');
    setIsFullscreen(false);
  };

  const closePreview = () => {
    setPreviewVariant(null);
    setIsFullscreen(false);
  };

  const toggleFullscreen = () => setIsFullscreen(!isFullscreen);

  const handleSelectTemplate = (variant: VariantInfo) => {
    if (variant.tier === 'premium' && variant.isTaken) {
      toast.error('Este template Premium já foi adquirido por outra empresa.');
      return;
    }

    localStorage.setItem('selectedPortalVariant', variant.variantId);

    if (variant.price > 0) {
      navigate(`/register?template=${variant.variantId}&paid=true`);
    } else {
      navigate(`/register?template=${variant.variantId}`);
    }
  };

  const visibleCategories = categories.slice(0, CATEGORIES_LIMIT);
  const hiddenCategoriesCount = Math.max(0, categories.length - CATEGORIES_LIMIT);
  const allCategories = categories;

  const allTags = getAllTags();
  const visibleTags = allTags.slice(0, TAGS_LIMIT);
  const hiddenTagsCount = Math.max(0, allTags.length - TAGS_LIMIT);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[#0A0A0A] text-white' : 'bg-[#FAFAFA] text-black'}`}>
        <div className="text-center font-mono text-xs tracking-widest uppercase">
          <div className="w-8 h-8 border-t-2 border-current rounded-full animate-spin mx-auto mb-4" />
          <span>Carregando websites...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[#0A0A0A] text-white' : 'bg-[#FAFAFA] text-black'}`}>
        <div className="text-center font-mono">
          <p className="text-red-500 text-sm mb-4">{error}</p>
          <button 
            onClick={loadVariants}
            className="px-6 py-2 border border-current text-xs uppercase tracking-widest hover:opacity-70 transition-all"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen font-sans transition-colors duration-500 selection:bg-neutral-500 selection:text-white ${
      isDarkMode ? 'bg-[#0D0D0D] text-[#EDEDED]' : 'bg-[#F5F5F7] text-[#111111]'
    }`}>
      
      {/* Dynamic Header with Theme Switcher */}
      <nav className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b transition-colors duration-300 ${
        isDarkMode ? 'bg-[#0D0D0D]/80 border-neutral-800/80' : 'bg-[#F5F5F7]/80 border-neutral-200/80'
      }`}>
        <div className="max-w-[1600px] mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs tracking-[0.25em] uppercase font-semibold">
              MPV
            </span>
            <span className={`text-[10px] font-mono px-2 py-0.5 border ${
              isDarkMode ? 'border-neutral-800 text-neutral-400' : 'border-neutral-300 text-neutral-600'
            }`}>
              {filteredVariants.length} Templates
            </span>
          </div>

          <div className="flex items-center gap-6">
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className={`p-2.5 rounded-full border transition-all duration-300 ${
                isDarkMode 
                  ? 'border-neutral-800 hover:border-neutral-600 text-neutral-300' 
                  : 'border-neutral-300 hover:border-neutral-500 text-neutral-700'
              }`}
              aria-label="Alternar Tema"
            >
              {isDarkMode ? <Sun size={15} /> : <Moon size={15} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section Minimalista e Impactante estilo Awwwards */}
      <header className="relative pt-36 pb-20 px-6 border-b border-neutral-800/30">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center gap-2 mb-6">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-neutral-400">
              Pagamentos Mpesa | EMola | Visa | Mastercard
            </span>
          </div>

          <h1 className="text-5xl md:text-8xl lg:text-[100px] font-bold tracking-tighter leading-[0.9] uppercase max-w-6xl mb-8">
            Websites com sistema de vendas integrado.
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-8 border-t border-neutral-800/20">
            <p className={`text-base md:text-lg font-light leading-relaxed md:col-span-2 ${
              isDarkMode ? 'text-neutral-400' : 'text-neutral-600'
            }`}>
              Coleção de plataformas integradas com ecossistema completo de vendas, faturação instantânea e gestão de métricas. Escolha um layout e comece a vender agora.
            </p>
          
          </div>
        </div>
      </header>

      {/* Control Bar: Categorias, Filtros e Busca em Layout Editorial */}
      <div className={`sticky top-20 z-40 backdrop-blur-xl border-b transition-colors duration-300 ${
        isDarkMode ? 'bg-[#0D0D0D]/90 border-neutral-800/80' : 'bg-[#F5F5F7]/90 border-neutral-200/80'
      }`}>
        <div className="max-w-[1600px] mx-auto px-6 py-4 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            
            {/* Categorias */}
            <div className="flex gap-2 items-center overflow-x-auto no-scrollbar pb-1 lg:pb-0">
              {visibleCategories.map(cat => {
                const categoryKey = cat === 'Todos' ? 'all' : cat.toLowerCase().trim();
                const isActive = selectedCategory === categoryKey;
                
                return (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(categoryKey)}
                    className={`text-xs font-mono tracking-wider uppercase py-2 px-4 transition-all duration-200 whitespace-nowrap border ${
                      isActive 
                        ? (isDarkMode ? 'border-white bg-white text-black' : 'border-black bg-black text-white')
                        : (isDarkMode ? 'border-neutral-800 text-neutral-400 hover:border-neutral-600 hover:text-white' : 'border-neutral-300 text-neutral-600 hover:border-neutral-400 hover:text-black')
                    }`}
                  >
                    {cat}
                  </button>
                );
              })}

              {hiddenCategoriesCount > 0 && (
                <div className="relative">
                  <button
                    ref={categoryToggleRef}
                    onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                    className={`flex items-center gap-1.5 px-3 py-2 border font-mono text-xs uppercase tracking-wider transition-all ${
                      isDarkMode ? 'border-neutral-800 text-neutral-400 hover:border-neutral-600' : 'border-neutral-300 text-neutral-600 hover:border-neutral-400'
                    }`}
                  >
                    +{hiddenCategoriesCount}
                    <ChevronDown size={14} className={`transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown is rendered via portal to document.body to avoid clipping/scrollbars from the parent container */}
                  {showCategoryDropdown && dropdownPos && createPortal(
                    <div
                      ref={categoryDropdownRef}
                      style={{ position: 'absolute', top: dropdownPos.top, left: dropdownPos.left, minWidth: dropdownPos.width }}
                      className={`mt-2 border shadow-2xl z-[1000] ${isDarkMode ? 'bg-[#141414] border-neutral-800' : 'bg-white border-neutral-200'}`}
                    >
                      <div className="p-2 space-y-1 max-h-[300px] overflow-y-auto">
                        {allCategories.slice(CATEGORIES_LIMIT).map(cat => {
                          const categoryKey = cat === 'Todos' ? 'all' : cat.toLowerCase().trim();
                          const isActive = selectedCategory === categoryKey;

                          return (
                            <button
                              key={cat}
                              onClick={() => {
                                setSelectedCategory(categoryKey);
                                setShowCategoryDropdown(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-xs font-mono uppercase tracking-wider transition-all ${
                                isActive
                                  ? (isDarkMode ? 'bg-white text-black' : 'bg-black text-white')
                                  : (isDarkMode ? 'text-neutral-400 hover:text-white hover:bg-neutral-800/50' : 'text-neutral-600 hover:text-black hover:bg-neutral-100')
                              }`}
                            >
                              {cat}
                            </button>
                          );
                        })}
                      </div>
                    </div>,
                    document.body
                  )}
                </div>
              )}
            </div>

            {/* Tier Select & Search */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              
              {/* Filter Tier */}
              <div className={`flex border p-0.5 ${isDarkMode ? 'border-neutral-800' : 'border-neutral-300'}`}>
                {(['all', 'freemium', 'premium'] as const).map(tier => (
                  <button
                    key={tier}
                    onClick={() => setSelectedTier(tier)}
                    className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest transition-all ${
                      selectedTier === tier
                        ? (isDarkMode ? 'bg-neutral-800 text-white' : 'bg-neutral-200 text-black')
                        : (isDarkMode ? 'text-neutral-500 hover:text-neutral-300' : 'text-neutral-500 hover:text-neutral-800')
                    }`}
                  >
                    {tier}
                  </button>
                ))}
              </div>

              {/* Search Bar */}
              <div className="relative group min-w-[240px]">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 transition-colors ${
                  isDarkMode ? 'text-neutral-600 group-focus-within:text-white' : 'text-neutral-400 group-focus-within:text-black'
                }`} size={14} />
                <input
                  type="text"
                  placeholder="Pesquisar design..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full border py-2 pl-9 pr-8 text-xs font-mono transition-all outline-none ${
                    isDarkMode 
                      ? 'bg-transparent border-neutral-800 text-white placeholder-neutral-600 focus:border-neutral-500' 
                      : 'bg-transparent border-neutral-300 text-black placeholder-neutral-400 focus:border-neutral-600'
                  }`}
                />
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-current"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Tags */}
          {allTags.length > 0 && (
            <div className={`flex items-center gap-3 pt-3 border-t ${isDarkMode ? 'border-neutral-900' : 'border-neutral-200'}`}>
              <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Tags:</span>
              <div className="flex gap-2 items-center overflow-x-auto no-scrollbar">
                {visibleTags.map(tag => {
                  const isSelected = selectedTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      onClick={() => toggleTag(tag)}
                      className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider transition-all border ${
                        isSelected
                          ? (isDarkMode ? 'border-white text-white' : 'border-black text-black')
                          : (isDarkMode ? 'border-neutral-800 text-neutral-500 hover:border-neutral-700' : 'border-neutral-200 text-neutral-500 hover:border-neutral-400')
                      }`}
                    >
                      #{tag}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Awwwards Style Showcase Grid */}
      <main className="max-w-[1600px] mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-x-8 gap-y-16">
          {filteredVariants.map((variant) => {
            const isPremiumTaken = variant.tier === 'premium' && variant.isTaken;

            return (
              <article
                key={variant._id}
                onClick={() => isPremiumTaken ? null : openPreview(variant)}
                className={`group cursor-pointer flex flex-col ${!variant.component || isPremiumTaken ? 'opacity-40 cursor-not-allowed' : ''}`}
              >
                {/* Media Container estilo Galeria */}
                <div className={`relative aspect-[16/10] overflow-hidden border transition-all duration-500 ${
                  isDarkMode 
                    ? 'bg-neutral-900 border-neutral-800 group-hover:border-neutral-600' 
                    : 'bg-neutral-200 border-neutral-300 group-hover:border-neutral-500'
                }`}>
                  {variant.previewImageUrl ? (
                    <img 
                      src={variant.previewImageUrl} 
                      alt={variant.name}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ease-out"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).src = '';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="font-mono text-5xl font-black opacity-10">{variant.name[0]}</span>
                    </div>
                  )}

                  {/* Badge Exclusiva no topo do Card */}
                  <div className="absolute top-4 left-4 z-10 flex gap-2">
                    <span className={`px-2 py-1 font-mono text-[9px] uppercase tracking-widest font-bold border backdrop-blur-md ${
                      variant.tier === 'premium'
                        ? 'bg-amber-500/10 text-amber-500 border-amber-500/30'
                        : 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30'
                    }`}>
                      {variant.tier}
                    </span>
                  </div>

                  {/* Hover Overlay estilo Awwwards SOTD */}
                  {variant.component && !isPremiumTaken && (
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center p-6">
                      <div className="flex items-center gap-2 border border-white text-white px-5 py-2.5 font-mono text-xs uppercase tracking-widest transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                        <span>Live Demo</span>
                        <ArrowUpRight size={14} />
                      </div>
                    </div>
                  )}

                  {isPremiumTaken && (
                    <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
                      <span className="font-mono text-xs uppercase tracking-widest text-neutral-400">Adquirido / Indisponível</span>
                    </div>
                  )}
                </div>

                {/* Details Section Editorial */}
                <div className="mt-5 flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-mono text-base font-bold uppercase tracking-tight group-hover:underline underline-offset-4 decoration-1">
                      {variant.name}
                    </h3>
                    <p className="text-xs font-mono uppercase tracking-wider text-neutral-500 mt-1">
                      {variant.category || 'Digital Architecture'}
                    </p>
                  </div>

                  <div className="text-right">
                    <span className="font-mono text-xs font-bold uppercase tracking-wider">
                      {variant.price > 0 ? `${variant.price} MT` : 'Free'}
                    </span>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </main>

      {/* Modal de Preview estilo Awwwards Frame */}
      {previewVariant && (
        <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-0 md:p-6">
          <div className={`bg-[#0A0A0A] text-white border border-neutral-800 w-full h-full flex flex-col transition-all duration-300 ${
            isFullscreen ? 'max-w-none max-h-none border-0' : 'max-w-[1700px] max-h-[95vh]'
          }`}>
            
            {/* Modal Topbar */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-800 bg-[#0A0A0A]">
              <div className="flex items-center gap-4">
                <div>
                  <h2 className="font-mono text-sm font-bold uppercase tracking-wider">
                    {previewVariant.name}
                  </h2>
                  <p className="text-xs text-neutral-500 font-mono hidden sm:block">
                    {previewVariant.description}
                  </p>
                </div>
              </div>

              {/* Viewport Selectors */}
              <div className="flex items-center gap-4">
                <div className="hidden md:flex border border-neutral-800 p-0.5">
                  <button 
                    onClick={() => setCurrentViewport('desktop')} 
                    className={`p-2 transition ${currentViewport === 'desktop' ? 'bg-neutral-800 text-white' : 'text-neutral-500 hover:text-white'}`}
                  >
                    <Monitor size={14} />
                  </button>
                  <button 
                    onClick={() => setCurrentViewport('tablet')} 
                    className={`p-2 transition ${currentViewport === 'tablet' ? 'bg-neutral-800 text-white' : 'text-neutral-500 hover:text-white'}`}
                  >
                    <Tablet size={14} />
                  </button>
                  <button 
                    onClick={() => setCurrentViewport('mobile')} 
                    className={`p-2 transition ${currentViewport === 'mobile' ? 'bg-neutral-800 text-white' : 'text-neutral-500 hover:text-white'}`}
                  >
                    <Smartphone size={14} />
                  </button>
                </div>

                <button
                  onClick={() => handleSelectTemplate(previewVariant)}
                  className="px-5 py-2 bg-white text-black font-mono text-xs uppercase tracking-widest font-bold hover:bg-neutral-200 transition-all"
                >
                  Usar Template
                </button>

                <div className="flex items-center gap-1 border-l border-neutral-800 pl-4">
                  <button 
                    onClick={toggleFullscreen} 
                    className="p-2 text-neutral-400 hover:text-white transition"
                  >
                    {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                  </button>
                  <button 
                    onClick={closePreview} 
                    className="p-2 text-neutral-400 hover:text-white transition"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>
            </div>

            {/* Render Window */}
            <div className="flex-1 overflow-auto bg-[#050505] flex justify-center items-center p-2">
              <div 
                className="transition-all duration-300 h-full w-full bg-black shadow-2xl border border-neutral-900 overflow-hidden"
                style={{
                  width: currentViewport === 'mobile' ? '390px' : 
                         currentViewport === 'tablet' ? '768px' : 
                         '100%',
                }}
              >
                <div className="w-full h-full overflow-y-auto">
                  <Suspense fallback={
                    <div className="h-full flex items-center justify-center font-mono text-xs uppercase tracking-widest text-neutral-500">
                      Carregando Preview...
                    </div>
                  }>
                    {previewVariant.component ? (
                      <previewVariant.component 
                        company={mockCompany} 
                        slug="preview-demo" 
                        services={mockServices}
                        products={mockProducts}
                        bundles={mockBundles}                        
                        viewport={currentViewport}
                      />
                    ) : (
                      <div className="h-full flex items-center justify-center font-mono text-xs text-neutral-500">
                        Preview Indisponível
                      </div>
                    )}
                  </Suspense>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer Minimalista */}
      <footer className={`py-20 border-t transition-colors duration-300 ${
        isDarkMode ? 'border-neutral-800/80 bg-[#0A0A0A]' : 'border-neutral-200 bg-[#EAEAEA]'
      }`}>
        <div className="max-w-[1600px] mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            <div className="col-span-1 md:col-span-2 space-y-4">
              <span className="font-mono text-xs uppercase tracking-[0.3em] font-bold">
                Meu Ponto de Venda
              </span>
              <p className="text-xs font-mono uppercase tracking-wider text-neutral-500 max-w-sm leading-relaxed">
                Plataforma de gestão comercial integrada, desde a Captação de leads, gestão de metas a sua primeira venda.
              </p>
            </div>

            <div>
              <h6 className="text-xs font-mono uppercase tracking-widest font-bold mb-4">Diretório</h6>
              <ul className="space-y-2 text-xs font-mono text-neutral-500">
                <li><Link to="/register" className="hover:underline">Activar POS</Link></li>
                <li><Link to="/register" className="hover:underline">Register</Link></li>
                <li><Link to="/" className="hover:underline">MPV</Link></li>
              </ul>
            </div>

            <div>
              <h6 className="text-xs font-mono uppercase tracking-widest font-bold mb-4">Informações</h6>
              <ul className="space-y-2 text-xs font-mono text-neutral-500">
                <li><Link to="/terms" className="hover:underline">Termos de Uso</Link></li>
                <li><Link to="/privacy" className="hover:underline">Privacidade</Link></li>
                <li className="pt-2 text-neutral-400">Maputo, Moçambique</li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-neutral-800/30 flex flex-col md:flex-row justify-between items-center gap-4 font-mono text-[10px] text-neutral-500 uppercase tracking-widest">
            <p>© {new Date().getFullYear()} MPV. All rights reserved.</p>
            <div className="flex gap-4">
              <Globe className="w-3.5 h-3.5 hover:text-current cursor-pointer transition" />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};